import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  ThemeProvider,
  Toolbar,
  Typography,
  createTheme,
} from "@mui/material";
import { ArrowBack, Edit } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { formatDate, formatDateTime } from "../../../services/formatDate";
import "react-toastify/dist/ReactToastify.css";
import { getClientById, unlinkClientFromVehicle } from "../../../api/clientApi";
import { getVehicleById } from "../../../api/vehicleApi";
import {
  formatDocument,
  formatPhone,
  maskDocument,
} from "../../../services/formatDocument";
import { toast } from "react-toastify";

const defaultTheme = createTheme({
  typography: {
    fontFamily: "Poppins, sans-serif",
  },
});

interface FieldMappings {
  [key: string]: string;
}
const fieldMappings: FieldMappings = {
  telefone_contato_cliente: "Telefone",
  documento_identificador_cliente: "Documento",
  data_recebimento_veiculo: "Data de Recebimento",
  codigo_radio_veiculo: "Código de Rádio",
  data_armazenagem_veiculo: "Data de Armazenagem",
  descricao_cor: "Cor",
  km_veiculo: "Km Inicial",
  ano_veiculo: "Ano do Veículo",
  km_final_veiculo: "Km Final",
  descricao_modelo: "Modelo",
  descricao_local: "Local de Armazenagem",
  descricao_ano_modelo: "Ano/Modelo",
  created_at: "Data de Criação",
  empresa_nome: "Empresa Localizada",
  permite_fotos_veiculo: "Permite uso das Fotos",
};

const ViewClientLinkedPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const { clientId } = useParams<{ clientId: string }>();
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const token = localStorage.getItem("token");
  const [clientData, setClientData] = useState<any>(null);
  const [vehicleData, setVehicleData] = useState<any>(null);

  useEffect(() => {
    const fetchVehicleData = async () => {
      try {
        if (token && clientId !== undefined) {
          const data = await getClientById(token, clientId);
          setClientData(data);
        } else {
          console.error("Token não encontrado ou clientId é indefinido");
        }
      } catch (error) {
        console.error("Erro ao buscar detalhes do veículo:", error);
      }
    };

    fetchVehicleData();
  }, [token, clientId]);

  useEffect(() => {
    const fetchVehicleData = async () => {
      try {
        if (token && vehicleId !== undefined) {
          const data = await getVehicleById(token, vehicleId);
          setVehicleData(data);
        } else {
          console.error("Token não encontrado ou vehicleId é indefinido");
        }
      } catch (error) {
        console.error("Erro ao buscar detalhes do veículo:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVehicleData();
  }, [token, vehicleId]);

  const handleEditClient = (clientId: number) => {
    navigate(`/editarCliente/${clientId}`);
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box sx={{ display: "flex" }}>
        <Box
          sx={{
            flexGrow: 1,
            overflow: "auto",
          }}
        >
          <Toolbar sx={{ mb: 2, mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                "&:hover": {
                  cursor: "pointer",
                  color: "red",
                },
              }}
              onClick={() => navigate("/veiculos")}
            >
              <IconButton
                edge="start"
                color="inherit"
                aria-label="voltar"
                sx={{ color: "inherit" }}
              >
                <ArrowBack />
              </IconButton>
              <Typography
                variant="h6"
                component="div"
                sx={{ color: "inherit", mt: 0.2 }}
              >
                Voltar
              </Typography>
            </Box>
          </Toolbar>

          <Container sx={{ mb: 4 }} maxWidth={"lg"}>
            <Box component="form" noValidate>
              <Typography
                variant="h5"
                fontStyle={"inherit"}
                sx={{ fontWeight: 400 }}
              >
                Cliente Vinculado
                <Divider
                  variant="middle"
                  sx={{
                    width: 120,
                    backgroundColor: "red",
                    height: 2,
                    marginLeft: 0,
                  }}
                />
              </Typography>
              <Grid sx={{ mt: 3 }} p={2} mr={4}>
                <Grid item xs={11} sm={10} md={8} lg={6}>
                  {loading ? (
                    <Skeleton
                      variant="rectangular"
                      height={300}
                      sx={{ borderRadius: 5 }}
                    />
                  ) : (
                    <Paper
                      elevation={4}
                      sx={{ p: 2, borderRadius: 5, width: "100%" }}
                    >
                      {vehicleData && (
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant="h5" mr={2}>
                              V.I.N:
                            </Typography>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: "bold" }}
                            >
                              {vehicleData.chassi_veiculo}
                            </Typography>
                          </Box>
                          <Divider sx={{ my: 2 }} />
                          <Grid container spacing={2}>
                            {Object.entries(vehicleData).map(([key, value]) => {
                              const mappedFieldName = fieldMappings[key] || key;

                              const excludedFields = [
                                "id",
                                "chassi_veiculo",
                                "id_cor",
                                "id_ano_modelo",
                                "id_cliente",
                                "acoes",
                                "fotos_km",
                                "id_modelo",
                                "id_local",
                                "fotos_codigo_radio",
                                "entrega_veiculo",
                                "cliente",
                                "nome_cliente",
                                "id_empresa",
                                "transferencia_veiculo",
                                "tem_checklist_agrupado",
                                "vendedor",
                                "id_cliente_vendedor",
                              ];

                              if (
                                !excludedFields.includes(key) &&
                                value !== null &&
                                value !== ""
                              ) {
                                let formattedValue: string;
                                if (typeof value === "string") {
                                  if (key === "data_recebimento_veiculo") {
                                    formattedValue = formatDateTime(value);
                                  } else if (
                                    key === "data_armazenagem_veiculo"
                                  ) {
                                    formattedValue = formatDate(value);
                                  } else if (key === "created_at") {
                                    formattedValue = formatDateTime(value);
                                  } else {
                                    formattedValue = value;
                                  }
                                } else if (typeof value === "boolean") {
                                  formattedValue = value ? "Sim" : "Não";
                                } else {
                                  formattedValue = String(value);
                                }

                                return (
                                  <Grid item xs={12} sm={6} md={6} key={key}>
                                    <Typography sx={{ fontWeight: "bold" }}>
                                      {mappedFieldName}:
                                    </Typography>
                                    <Typography variant="body1">
                                      {formattedValue}
                                    </Typography>
                                  </Grid>
                                );
                              }
                              return null;
                            })}
                          </Grid>
                        </Box>
                      )}
                    </Paper>
                  )}
                </Grid>
              </Grid>
              <Grid p={2} mr={4}>
                <Grid item xs={11} sm={10} md={8} lg={6}>
                  {loading ? (
                    <Skeleton
                      variant="rectangular"
                      height={100}
                      sx={{ borderRadius: 5 }}
                    />
                  ) : (
                    <Paper
                      elevation={4}
                      sx={{ p: 2, mb: 2, borderRadius: 5, width: "100%" }}
                    >
                      {clientData && (
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", sm: "row" },
                              alignItems: { xs: "flex-start", sm: "center" },
                              flexWrap: "wrap",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 1,
                                flexGrow: 1,
                              }}
                            >
                              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                Nome:
                              </Typography>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: "bold",
                                  wordBreak: "break-word",
                                }}
                              >
                                {clientData.nome_cliente}
                              </Typography>
                              <IconButton
                                aria-label="editar"
                                onClick={() => handleEditClient(clientData.id)}
                                size="small"
                              >
                                <Edit color="primary" />
                              </IconButton>
                            </Box>

                            <Button
                              onClick={async () => {
                                if (!token || !vehicleId) return;

                                try {
                                  await unlinkClientFromVehicle(
                                    token,
                                    vehicleId
                                  );
                                  toast.success(
                                    "Cliente desvinculado com sucesso!"
                                  );
                                  navigate(`/cliente/${vehicleId}`);
                                } catch (error) {
                                  toast.error("Erro ao desvincular cliente.");
                                }
                              }}
                              sx={{
                                backgroundColor: "#d32f2f",
                                color: "#fff",
                                border: "none",
                                padding: "6px 12px",
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontWeight: "bold",
                                fontSize: "0.875rem",
                                alignSelf: { xs: "stretch", sm: "center" },
                                mt: { xs: 1, sm: 0 },
                              }}
                            >
                              Desvincular Cliente
                            </Button>
                          </Box>

                          <Divider sx={{ my: 2 }} />
                          <Grid container spacing={2}>
                            {Object.entries(clientData).map(([key, value]) => {
                              const mappedFieldName = fieldMappings[key] || key;

                              const excludedFields = [
                                "id",
                                "id_empresa",
                                "nome_cliente",
                                "veiculos",
                                "veiculos_relacao",
                                "codigo_dms_cliente",
                              ];

                              if (!excludedFields.includes(key)) {
                                let formattedValue: string;
                                if (typeof value === "string") {
                                  if (
                                    key === "documento_identificador_cliente"
                                  ) {
                                    formattedValue = maskDocument(value);
                                  } else if (
                                    key === "telefone_contato_cliente"
                                  ) {
                                    formattedValue = formatPhone(value);
                                  } else {
                                    formattedValue = value;
                                  }
                                } else {
                                  formattedValue = String(value);
                                }

                                return (
                                  <Grid item xs={12} sm={6} md={6} key={key}>
                                    <Typography sx={{ fontWeight: "bold" }}>
                                      {mappedFieldName}:
                                    </Typography>
                                    <Typography variant="body1">
                                      {formattedValue}
                                    </Typography>
                                  </Grid>
                                );
                              }
                              return null;
                            })}
                          </Grid>
                        </Box>
                      )}
                    </Paper>
                  )}
                </Grid>
              </Grid>
              {vehicleData?.vendedor && (
                <Grid p={2} mr={4}>
                  <Grid item xs={11} sm={10} md={8} lg={8}>
                    <Paper
                      elevation={4}
                      sx={{ p: 2, mb: 2, borderRadius: 5, width: "100%" }}
                    >
                      <Typography variant="h6" gutterBottom>
                        Vendedor Vinculado
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography sx={{ fontWeight: "bold" }}>
                            Nome:
                          </Typography>
                          <Typography>
                            {vehicleData.vendedor.nome_cliente}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography sx={{ fontWeight: "bold" }}>
                            Documento:
                          </Typography>
                          <Typography>
                            {formatDocument(
                              vehicleData.vendedor
                                .documento_identificador_cliente
                            )}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography sx={{ fontWeight: "bold" }}>
                            Telefone:
                          </Typography>
                          <Typography>
                            {formatPhone(
                              vehicleData.vendedor.telefone_contato_cliente
                            )}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default ViewClientLinkedPage;
